import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  usePrintSelection,
  PRINT_SIZES,
  PRINT_FINISHES,
  finishLabel,
  type PrintLine,
  type ScanMeta,
} from '../contexts/PrintSelectionContext';
import type { ScansResponse } from '../types/roll';
import {
  usePrintPricing,
  isPricingUnavailable as isPricingUnavailableError,
  discountPercent,
  estimatedTotal,
  unitPrice,
  gbp,
} from '../hooks/usePrintPricing';
import { usePageTitle } from '../hooks/usePageTitle';
import { PrintCropDialog } from '../components/PrintCropDialog';
import { VolumeDiscountTable } from '../components/VolumeDiscountTable';
import {
  framingStyle,
  hasLetterboxing,
  paperAspect,
  refitCrop,
} from '../utils/printGeometry';
import type { CropSpec } from '../utils/editRecipe';
import styles from './PrintSelectionPage.module.css';

type OrderStatus = 'PENDING_PAYMENT' | 'PAID' | 'TIMEOUT';

interface CreateOrderResponse {
  print_order_id: string;
  checkout_url: string;
}

interface OrderStatusResponse {
  status: OrderStatus;
  shopify_order_number?: string;
}

type PageState =
  | { phase: 'selection' }
  | { phase: 'creating' }
  | { phase: 'polling'; printOrderId: string }
  | { phase: 'confirmed'; orderNumber: string }
  | { phase: 'error'; message: string; canRetry: boolean };

function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

// ─── Paper + border guidance ─────────────────────────────────────────────────
//
// Customers pick a finish from a four-item dropdown with no way to feel the
// paper, so the bulk bar carries a short guide. Keyed by finish id (not by
// index) so it stays correct whatever order the pricing endpoint returns
// `finishes` in, and an unrecognised finish simply renders without a
// description rather than mislabelling one.

const FINISH_GUIDE: Record<string, string> = {
  gloss:
    'High shine, deep blacks and punchy colour. The most reflective of the four — prints catch the light and show fingerprints most readily.',
  lustre:
    'A fine pearl-like sheen sitting between gloss and matte. Rich colour and contrast with far less glare, and it resists fingerprints. The safe all-rounder.',
  matte:
    'No shine at all. Softer contrast and a flatter, more muted look, with no reflections at any viewing angle — easiest to look at in a bright room.',
  fine_matte:
    'Matte with a subtle fine-grain texture. Keeps the glare-free surface but holds a little more depth and detail than plain matte.',
};

/** Approximate white border width, in millimetres, when the border is added. */
const BORDER_WIDTH_MM = 5;

/**
 * Aspect a frame is assumed to have until its thumbnail reports otherwise.
 *
 * Thumbnails load asynchronously, and until one does we have no idea whether a
 * scan is landscape, portrait or square. 3:2 is the overwhelmingly common
 * 35mm frame, so it is the least surprising placeholder — and it is only ever
 * used to draw the preview box, never to build a crop.
 */
const ASSUMED_IMAGE_ASPECT = 3 / 2;

// ─── Pending (in-flight) order marker ────────────────────────────────────────
//
// Written just before redirecting to Shopify checkout. A customer who
// browser-backs out of the payment screen returns via a full page load — all
// in-memory state (including the "Waiting for payment" polling phase) is gone.
// The marker lets the page offer to resume the unpaid order instead of leaving
// the customer to unknowingly create a duplicate draft by checking out again.

const PENDING_ORDER_KEY = 'loupe_pending_print_order';
/** Matches the backend's 30-minute payment window on the status endpoint. */
const PENDING_ORDER_MAX_AGE_MS = 30 * 60 * 1000;

interface PendingPrintOrder {
  print_order_id: string;
  checkout_url: string;
  created_at: number;
}

function readPendingOrder(): PendingPrintOrder | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ORDER_KEY);
    if (!raw) return null;
    const marker = JSON.parse(raw) as Partial<PendingPrintOrder>;
    if (
      typeof marker.print_order_id !== 'string' ||
      typeof marker.checkout_url !== 'string' ||
      typeof marker.created_at !== 'number' ||
      Date.now() - marker.created_at > PENDING_ORDER_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(PENDING_ORDER_KEY);
      return null;
    }
    return marker as PendingPrintOrder;
  } catch {
    return null;
  }
}

function writePendingOrder(marker: PendingPrintOrder): void {
  try {
    sessionStorage.setItem(PENDING_ORDER_KEY, JSON.stringify(marker));
  } catch {
    // sessionStorage unavailable — the resume banner simply won't appear.
  }
}

function clearPendingOrder(): void {
  try {
    sessionStorage.removeItem(PENDING_ORDER_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

async function createPrintOrder(
  items: Array<{
    scan_id: string;
    size: string;
    finish: string;
    border: boolean;
    crop?: CropSpec;
    quantity: number;
  }>,
  idempotencyKey: string,
): Promise<CreateOrderResponse> {
  const res = await fetch('/api/v1/print/orders', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, idempotency_key: idempotencyKey }),
  });
  if (res.status === 502) throw new Error('ORDER_CREATION_FAILED');
  if (!res.ok) throw new Error(`Failed to create print order (${res.status})`);
  return res.json();
}

async function pollOrderStatus(printOrderId: string): Promise<OrderStatusResponse> {
  const res = await fetch(`/api/v1/print/orders/${printOrderId}/status`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to poll order status (${res.status})`);
  return res.json();
}

/**
 * Where "back" goes from the basket.
 *
 * Derived from the selection itself, because router state proved too fragile to
 * rely on: it is absent when /print is reached from the header nav, and lost on
 * any full page load — which is exactly what returning from Shopify checkout
 * is. Customers ended up on the order list having to navigate back down to the
 * gallery they had just left.
 *
 * The selection knows which gallery its images came from, so when they all come
 * from one — very nearly always — that gallery is the answer, and it survives a
 * reload because hydration re-derives it. The order list is kept only for the
 * cases where there genuinely is no single answer: a basket spanning several
 * galleries, an empty basket, or the instant before hydration completes. Router
 * state is still honoured when present, since it names where the customer
 * actually came from.
 */
function useBackToGallery(lines: PrintLine[]): { to: string; label: string } {
  const location = useLocation();
  const fromState = (location.state as { fromGallery?: string } | null)?.fromGallery;
  if (fromState) return { to: fromState, label: '← Back to gallery' };

  const orderIds = new Set(lines.map((l) => l.order_id).filter(Boolean));
  if (orderIds.size === 1) {
    const [orderId] = [...orderIds];
    return { to: `/orders/${orderId}`, label: '← Back to gallery' };
  }
  return { to: '/orders', label: '← Back to your galleries' };
}

export function PrintSelectionPage() {
  usePageTitle('Print selection');
  const {
    lines,
    totalQuantity,
    updateLine,
    removeLine,
    applyToAll,
    clearSelection,
    hydrateFromScans,
  } = usePrintSelection();
  const [pageState, setPageState] = useState<PageState>({ phase: 'selection' });
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey);
  const [pendingOrder, setPendingOrder] = useState<PendingPrintOrder | null>(readPendingOrder);
  // Real frame aspects, learned from each thumbnail as it loads and keyed by
  // scan so the same frame in two lines shares one measurement.
  const [imageAspects, setImageAspects] = useState<Record<string, number>>({});
  const [croppingLineId, setCroppingLineId] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hydrationAttempted = useRef(false);

  const {
    data: pricing,
    isLoading: pricingLoading,
    isError: pricingError,
    error: pricingErr,
  } = usePrintPricing();

  const isPricingUnavailable = pricingError && isPricingUnavailableError(pricingErr);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // A selection restored from the server carries only scan_id/size/finish/
  // quantity; the gallery normally re-hydrates filenames and (expiring, so
  // never persisted) thumbnail URLs. On a hard load of /print the gallery was
  // never visited, so fetch the customer's scans here and hydrate directly —
  // otherwise the basket renders raw scan UUIDs and grey placeholders.
  const needsHydration = lines.some((l) => !l.thumbnail_url);
  useEffect(() => {
    if (!needsHydration || hydrationAttempted.current) return;
    hydrationAttempted.current = true;
    let cancelled = false;
    (async () => {
      try {
        const ordersRes = await fetch('/api/v1/orders', { credentials: 'include' });
        if (!ordersRes.ok) return;
        const ordersData: { orders?: Array<{ order_id: string }> } = await ordersRes.json();
        const metas: ScanMeta[] = [];
        for (const order of ordersData.orders ?? []) {
          if (cancelled) return;
          const scansRes = await fetch(`/api/v1/orders/${order.order_id}/scans`, {
            credentials: 'include',
          });
          if (!scansRes.ok) continue;
          const scansData: ScansResponse = await scansRes.json();
          for (const roll of scansData.rolls ?? []) {
            for (const scan of roll.scans) {
              metas.push({
                scan_id: scan.scan_id,
                filename: scan.original_filename,
                thumbnail_url: scan.thumbnail_url,
                roll_id: roll.roll_id,
                order_id: order.order_id,
              });
            }
          }
        }
        if (!cancelled && metas.length > 0) hydrateFromScans(metas);
      } catch {
        // Hydration is cosmetic; the basket still works with placeholders.
      }
    })();
    return () => {
      cancelled = true;
    };
    // hydrateFromScans gets a fresh identity every render; the effect is keyed
    // off whether hydration is needed and runs at most once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsHydration]);

  const startPolling = useCallback((printOrderId: string) => {
    setPageState({ phase: 'polling', printOrderId });
    pollingRef.current = setInterval(async () => {
      try {
        const result = await pollOrderStatus(printOrderId);
        if (result.status === 'PAID') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          clearPendingOrder();
          setPendingOrder(null);
          // The basket is now a paid order. The server clears the persisted
          // selection on the same transition; this drops it from the open page
          // too, so nothing invites a second purchase of the same prints.
          clearSelection();
          setPageState({
            phase: 'confirmed',
            orderNumber: result.shopify_order_number ?? printOrderId,
          });
        } else if (result.status === 'TIMEOUT') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          clearPendingOrder();
          setPendingOrder(null);
          setPageState({
            phase: 'error',
            message:
              'Payment confirmation timed out. If you completed payment, your order is still being processed.',
            canRetry: true,
          });
        }
      } catch (err) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPageState({
          phase: 'error',
          message: err instanceof Error ? err.message : 'Failed to check payment status',
          canRetry: true,
        });
      }
    }, 5000);
    // `clearSelection` is a fresh closure each render but only ever calls the
    // reducer's stable dispatch, so capturing an old one is harmless. Listing it
    // would rebuild this callback every render and with it handleConfirmOrder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmOrder = useCallback(async () => {
    if (lines.length === 0) return;
    setPageState({ phase: 'creating' });
    const items = lines.map((l) => ({
      scan_id: l.scan_id,
      size: l.size,
      finish: l.finish,
      border: l.border,
      // Omitted for a letterbox: the backend models "no crop" as an absent
      // optional, and fitting the whole frame is the absence of a crop.
      ...(l.crop ? { crop: l.crop } : {}),
      quantity: l.quantity,
    }));
    try {
      const { print_order_id, checkout_url } = await createPrintOrder(items, idempotencyKey);
      // Record the in-flight order first: if the customer browser-backs out of
      // the payment screen the page reloads with no memory of this order, and
      // the marker is what lets the selection view offer to resume it.
      writePendingOrder({
        print_order_id,
        checkout_url,
        created_at: Date.now(),
      });
      // Redirect in the same tab (no popup blocker); status polling resumes when
      // the customer returns. (Phase 2 replaces polling with an orders/paid webhook.)
      window.location.href = checkout_url;
      startPolling(print_order_id);
    } catch (err) {
      const message =
        err instanceof Error && err.message === 'ORDER_CREATION_FAILED'
          ? 'Order could not be placed. Please try again.'
          : err instanceof Error
            ? err.message
            : 'Something went wrong creating your order';
      setPageState({ phase: 'error', message, canRetry: true });
    }
  }, [lines, idempotencyKey, startPolling]);

  const recordImageAspect = useCallback((scanId: string, width: number, height: number) => {
    if (!width || !height) return;
    setImageAspects((prev) =>
      prev[scanId] ? prev : { ...prev, [scanId]: width / height },
    );
  }, []);

  // A crop is shaped to one paper aspect, so a size change would otherwise
  // invalidate it. Re-fit it instead: the customer's centre and zoom survive and
  // only the rectangle's edges move. Both fields go in one patch so the basket
  // never renders a line whose crop belongs to the previous size.
  const changeSize = useCallback(
    (line: PrintLine, size: string) => {
      if (!line.crop) {
        updateLine(line.id, { size });
        return;
      }
      const imageAspect = imageAspects[line.scan_id] ?? ASSUMED_IMAGE_ASPECT;
      const crop = refitCrop(
        line.crop,
        paperAspect(line.size, imageAspect),
        paperAspect(size, imageAspect),
        imageAspect,
      );
      updateLine(line.id, { size, crop });
    },
    [imageAspects, updateLine],
  );

  const handleRetry = useCallback(() => {
    setIdempotencyKey(generateIdempotencyKey());
    setPageState({ phase: 'selection' });
  }, []);

  // Resume an order interrupted by backing out of the payment screen: check its
  // status first — it may have been paid in another tab or timed out — and only
  // return to the (reusable) Shopify invoice when it is genuinely still unpaid.
  const handleResumeCheckout = useCallback(async () => {
    if (!pendingOrder) return;
    try {
      const result = await pollOrderStatus(pendingOrder.print_order_id);
      if (result.status === 'PAID') {
        clearPendingOrder();
        setPendingOrder(null);
        clearSelection();
        setPageState({
          phase: 'confirmed',
          orderNumber: result.shopify_order_number ?? pendingOrder.print_order_id,
        });
        return;
      }
      if (result.status === 'TIMEOUT') {
        clearPendingOrder();
        setPendingOrder(null);
        setPageState({
          phase: 'error',
          message:
            'Your earlier checkout expired before payment was taken. Your selection is unchanged — you can check out again.',
          canRetry: true,
        });
        return;
      }
    } catch {
      // Status check failed; the invoice link below is still the safest
      // destination — Shopify will show the order's real state.
    }
    window.location.href = pendingOrder.checkout_url;
    // clearSelection omitted for the same reason as in startPolling above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrder]);

  const handleDismissPending = useCallback(() => {
    clearPendingOrder();
    setPendingOrder(null);
  }, []);

  const back = useBackToGallery(lines);
  const pct = discountPercent(totalQuantity, pricing?.volume_discounts);
  const finishes = pricing?.finishes ?? [...PRINT_FINISHES];
  // Resolved from the live list, so removing a line closes its dialog rather
  // than leaving it framing something no longer in the basket.
  const croppingLine = lines.find((l) => l.id === croppingLineId) ?? null;

  // ─── Confirmed ─────────────────────────────────────────────────────────────
  if (pageState.phase === 'confirmed') {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Print Order</h1>
        <div className={styles.confirmation} role="status" aria-live="polite">
          <h2 className={styles.confirmationHeading}>Order confirmed</h2>
          <p className={styles.confirmationText}>
            Your print order <span className={styles.orderNumber}>{pageState.orderNumber}</span> has
            been confirmed. We'll get your prints ready.
          </p>
          <Link to="/orders" className={styles.confirmationLink}>
            Back to orders
          </Link>
        </div>
      </main>
    );
  }

  // ─── Creating / polling ────────────────────────────────────────────────────
  if (pageState.phase === 'creating' || pageState.phase === 'polling') {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Print Order</h1>
        <div className={styles.progress} role="status" aria-live="polite">
          <div className={styles.progressSpinner} aria-hidden="true" />
          <p className={styles.progressText}>
            {pageState.phase === 'creating' ? 'Creating your order...' : 'Waiting for payment...'}
          </p>
        </div>
      </main>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (pageState.phase === 'error') {
    return (
      <main className={styles.page}>
        {/* An error is exactly where a customer most needs a way out that
            isn't the browser's back button. */}
        <Link to={back.to} className={styles.backLink}>
          {back.label}
        </Link>
        <h1 className={styles.heading}>Print Order</h1>
        <div className={styles.error} role="alert">
          <p className={styles.errorText}>{pageState.message}</p>
          {pageState.canRetry && (
            <button type="button" className={styles.retryBtn} onClick={handleRetry}>
              Retry
            </button>
          )}
        </div>
      </main>
    );
  }

  // ─── Selection (default) ───────────────────────────────────────────────────
  const canConfirm = lines.length > 0 && !pricingError && !pricingLoading;
  const total = estimatedTotal(lines, pricing, pct);

  return (
    <main className={styles.page}>
      <Link to={back.to} className={styles.backLink}>
        {back.label}
      </Link>
      <h1 className={styles.heading}>Print Selection</h1>
      <p className={styles.subheading}>
        {lines.length} {lines.length === 1 ? 'image' : 'images'} · {totalQuantity}{' '}
        {totalQuantity === 1 ? 'print' : 'prints'}
      </p>

      {pendingOrder && (
        <div className={styles.pendingBanner} role="status">
          <p className={styles.pendingText}>
            You have an unpaid print order from earlier. Resume checkout to pay for it, or
            dismiss it and keep editing — checking out again places a new order.
          </p>
          <div className={styles.pendingActions}>
            <button type="button" className={styles.resumeBtn} onClick={handleResumeCheckout}>
              Resume checkout
            </button>
            <button type="button" className={styles.pendingDismissBtn} onClick={handleDismissPending}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {pricingLoading && <p className={styles.loading}>Loading pricing...</p>}
      {isPricingUnavailable && (
        <div className={styles.pricingError} role="alert">
          Pricing is temporarily unavailable. Please try again later.
        </div>
      )}
      {pricingError && !isPricingUnavailable && (
        <div className={styles.pricingError} role="alert">
          Unable to load pricing. Please refresh the page.
        </div>
      )}

      {lines.length === 0 ? (
        <p className={styles.emptyState}>
          No images selected for printing.{' '}
          <Link to="/orders">Browse your galleries</Link> to add images.
        </p>
      ) : (
        <>
          {/* Bulk controls */}
          <div className={styles.bulkBar}>
            <div className={styles.bulkControls}>
              <label className={styles.bulkControl}>
                Apply size to all
                <select
                  className={styles.select}
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) applyToAll({ size: e.target.value });
                    e.target.value = '';
                  }}
                  aria-label="Apply a size to all images"
                >
                  <option value="" disabled>
                    Choose…
                  </option>
                  {PRINT_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s.replace('x', '×')}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.bulkControl}>
                Apply finish to all
                <select
                  className={styles.select}
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) applyToAll({ finish: e.target.value });
                    e.target.value = '';
                  }}
                  aria-label="Apply a finish to all images"
                >
                  <option value="" disabled>
                    Choose…
                  </option>
                  {finishes.map((f) => (
                    <option key={f} value={f}>
                      {finishLabel(f)}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.bulkControl}>
                Apply border to all
                <select
                  className={styles.select}
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) applyToAll({ border: e.target.value === 'border' });
                    e.target.value = '';
                  }}
                  aria-label="Apply a border choice to all images"
                >
                  <option value="" disabled>
                    Choose…
                  </option>
                  <option value="border">Add {BORDER_WIDTH_MM}mm white border</option>
                  <option value="none">No border</option>
                </select>
              </label>
              <button type="button" className={styles.clearBtn} onClick={clearSelection}>
                Clear all
              </button>
            </div>

            <details className={styles.guide}>
              <summary className={styles.guideSummary}>
                Paper finishes and borders explained
              </summary>
              <dl className={styles.guideList}>
                {finishes.map((f) =>
                  FINISH_GUIDE[f] ? (
                    <div key={f} className={styles.guideEntry}>
                      <dt className={styles.guideTerm}>{finishLabel(f)}</dt>
                      <dd className={styles.guideText}>{FINISH_GUIDE[f]}</dd>
                    </div>
                  ) : null,
                )}
                <div className={styles.guideEntry}>
                  <dt className={styles.guideTerm}>Border</dt>
                  <dd className={styles.guideText}>
                    Adding a border prints an approximately {BORDER_WIDTH_MM}mm white margin
                    around your image on the paper. The paper size is unchanged — the image is
                    printed a little smaller so the white edge fits inside it. Bordered and
                    borderless prints cost the same.
                  </dd>
                </div>
              </dl>
            </details>
          </div>

          <div className={styles.itemList}>
            {lines.map((line) => {
              const unit = unitPrice(line.size, pricing, pct);
              const imageAspect = imageAspects[line.scan_id] ?? ASSUMED_IMAGE_ASPECT;
              const target = paperAspect(line.size, imageAspect);
              const measured = imageAspects[line.scan_id] !== undefined;
              const letterboxed = !line.crop && measured && hasLetterboxing(imageAspect, target);
              const sizeLabel = line.size.replace('x', '×');
              return (
                <div key={line.id} className={styles.item}>
                  {/*
                    The preview box is the sheet of paper: it carries the print's
                    aspect and a white ground, so a letterboxed frame shows its
                    white margins here exactly as it will in the hand.
                  */}
                  <div
                    className={styles.preview}
                    style={{ aspectRatio: `${target}` }}
                    data-testid={`preview-${line.id}`}
                  >
                    {line.thumbnail_url ? (
                      <img
                        src={line.thumbnail_url}
                        alt={line.filename}
                        className={styles.previewImage}
                        style={framingStyle(line.crop, imageAspect, target)}
                        loading="lazy"
                        onLoad={(e) =>
                          recordImageAspect(
                            line.scan_id,
                            e.currentTarget.naturalWidth,
                            e.currentTarget.naturalHeight,
                          )
                        }
                      />
                    ) : (
                      <div className={styles.thumbnailPlaceholder} aria-hidden="true" />
                    )}
                  </div>
                  <div className={styles.itemDetails}>
                    <p className={styles.filename}>{line.filename}</p>
                    <p className={styles.framingNote}>
                      {line.crop
                        ? `Cropped to fill the ${sizeLabel}`
                        : letterboxed
                          ? `Whole frame on ${sizeLabel}, with white edges`
                          : `Whole frame on ${sizeLabel}`}
                    </p>
                    <button
                      type="button"
                      className={styles.framingBtn}
                      onClick={() => setCroppingLineId(line.id)}
                      disabled={!line.thumbnail_url}
                      aria-label={`Adjust framing for ${line.filename}`}
                    >
                      Adjust framing
                    </button>
                  </div>
                  <div className={styles.itemControls}>
                    <select
                      className={styles.select}
                      value={line.size}
                      onChange={(e) => changeSize(line, e.target.value)}
                      aria-label={`Size for ${line.filename}`}
                    >
                      {PRINT_SIZES.map((s) => (
                        <option key={s} value={s}>
                          {s.replace('x', '×')}
                        </option>
                      ))}
                    </select>
                    <select
                      className={styles.select}
                      value={line.finish}
                      onChange={(e) => updateLine(line.id, { finish: e.target.value })}
                      aria-label={`Finish for ${line.filename}`}
                    >
                      {finishes.map((f) => (
                        <option key={f} value={f}>
                          {finishLabel(f)}
                        </option>
                      ))}
                    </select>
                    <label className={styles.borderToggle}>
                      <input
                        type="checkbox"
                        checked={line.border}
                        onChange={(e) => updateLine(line.id, { border: e.target.checked })}
                        aria-label={`Add a ${BORDER_WIDTH_MM}mm white border to ${line.filename}`}
                      />
                      Border
                    </label>
                    <input
                      type="number"
                      className={styles.quantityInput}
                      value={line.quantity}
                      min={1}
                      max={99}
                      step={1}
                      onChange={(e) =>
                        updateLine(line.id, { quantity: parseInt(e.target.value, 10) || 1 })
                      }
                      aria-label={`Quantity for ${line.filename}`}
                    />
                    <span className={styles.itemPrice}>
                      {unit != null ? gbp(unit * line.quantity) : '—'}
                    </span>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => removeLine(line.id)}
                      aria-label={`Remove ${line.filename}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {pricing && <VolumeDiscountTable pricing={pricing} totalQuantity={totalQuantity} />}

          {total != null && (
            <div className={styles.summary}>
              <div className={styles.summaryLines}>
                {pct > 0 && (
                  <span className={styles.discountNote}>
                    {pct}% volume discount applied ({totalQuantity} prints)
                  </span>
                )}
                <span className={styles.estimateNote}>
                  Estimated — final total applied at checkout
                </span>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Estimated total</span>
                <span className={styles.totalPrice}>{gbp(total)}</span>
              </div>
            </div>
          )}

          <button
            type="button"
            className={styles.confirmBtn}
            onClick={handleConfirmOrder}
            disabled={!canConfirm}
            aria-label="Confirm order and go to checkout"
          >
            Checkout
          </button>
        </>
      )}

      {croppingLine && croppingLine.thumbnail_url && (
        <PrintCropDialog
          filename={croppingLine.filename}
          imageUrl={croppingLine.thumbnail_url}
          imageAspect={imageAspects[croppingLine.scan_id] ?? ASSUMED_IMAGE_ASPECT}
          targetAspect={paperAspect(
            croppingLine.size,
            imageAspects[croppingLine.scan_id] ?? ASSUMED_IMAGE_ASPECT,
          )}
          sizeLabel={croppingLine.size.replace('x', '×')}
          crop={croppingLine.crop}
          onCancel={() => setCroppingLineId(null)}
          onApply={(crop) => {
            updateLine(croppingLine.id, { crop });
            setCroppingLineId(null);
          }}
        />
      )}
    </main>
  );
}
